// J21-M15 + APL-11850 layered recovery: a failed chunk import retries on a
// capped backoff ladder REGARDLESS of navigator.onLine (captive/dead-WAN
// networks report online), each gap cut short by the 'online' event. Chromium
// memoizes a failed module fetch by URL, so every retry imports a fresh
// cache-busted URL (extracted from the failure message) — a plain re-import
// never touches the network again. A final failure is marked
// (isChunkLoadError) and parks a reset so resetFailedChunkImports() can mint
// a fresh inner lazy — React.lazy also caches rejections permanently.
import {
  createElement,
  forwardRef,
  lazy,
  type ComponentProps,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';

export type PreloadableLazyComponent<T extends ComponentType<any> = ComponentType<any>> =
  ComponentType<ComponentProps<T>> & { preload: () => Promise<void> };

// Capped ladder — a truly-dead network settles into the nearest error
// boundary after ~22.5s; a stale-deploy 404 still ends there (accepted).
const RETRY_DELAYS_MS = [1500, 3000, 6000, 12000];

const CHUNK_ERROR_FLAG = 'apChunkLoadError';

const markChunkLoadError = (error: unknown): void => {
  if (error instanceof Error) {
    try {
      (error as Error & Record<string, unknown>)[CHUNK_ERROR_FLAG] = true;
    } catch {
      // frozen error — the message pattern below still matches native imports
    }
  }
};

// Native dynamic-import failure messages (Chrome / Firefox / Safari / webpack).
const CHUNK_MESSAGE_PATTERN =
  /dynamically imported module|Importing a module script failed|Loading chunk \S+ failed/i;

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  ((error as Error & Record<string, unknown>)[CHUNK_ERROR_FLAG] === true ||
    CHUNK_MESSAGE_PATTERN.test(error.message));

/** Chrome/Firefox import failures carry the chunk URL — Safari's doesn't
 *  (WebKit doesn't memoize failed imports, so its plain retries re-fetch). */
export const extractModuleUrl = (error: unknown): string | null =>
  error instanceof Error ? error.message.match(/https?:\/\/[^\s'"]+\.m?js\b/)?.[0] ?? null : null;

// Seam: jsdom can't intercept native import() of a URL — tests stub this.
export const nativeImport = {
  load: (url: string): Promise<unknown> => import(/* @vite-ignore */ url),
};

// Exhausted instances park their reset here; a chunk error boundary retries by
// resetting all failed chunks, then re-mounting its subtree.
const failedResets = new Set<() => void>();

export const resetFailedChunkImports = (): void => {
  const resets = [...failedResets];
  failedResets.clear();
  for (const reset of resets) reset();
};

/** Resolves after `ms`, or immediately on the next 'online' event. */
const onlineOrTimeout = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined') {
      setTimeout(resolve, ms);
      return;
    }
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    window.addEventListener('online', done);
  });

export function lazyWithRetry<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>
): PreloadableLazyComponent<T>;
export function lazyWithRetry<T extends ComponentType<any>, M>(
  load: () => Promise<M>,
  // Named-export mapping stays OUTSIDE the thunk so cache-busted re-imports
  // (raw namespace) can be mapped too.
  pick: (module: M) => { default: T }
): PreloadableLazyComponent<T>;
export function lazyWithRetry<T extends ComponentType<any>, M>(
  load: () => Promise<M>,
  pick?: (module: M) => { default: T }
): PreloadableLazyComponent<T> {
  let pending: Promise<{ default: T }> | null = null;
  let moduleUrl: string | null = null;
  let bustSeq = 0;

  const toModule = (ns: M): { default: T } => (pick ? pick(ns) : (ns as { default: T }));

  const attemptLoad = async (retrying: boolean): Promise<{ default: T }> => {
    if (retrying && moduleUrl) {
      bustSeq += 1; // every retry needs an unseen URL — busted failures memoize too
      const sep = moduleUrl.includes('?') ? '&' : '?';
      const ns = (await nativeImport.load(`${moduleUrl}${sep}ap-retry=${bustSeq}`)) as M;
      const mod = toModule(ns);
      if (!mod?.default) throw new Error(`ap-retry import missing default export: ${moduleUrl}`);
      return mod;
    }
    return toModule(await load());
  };

  const loadWithBackoff = async (): Promise<{ default: T }> => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
      if (attempt > 0) await onlineOrTimeout(RETRY_DELAYS_MS[attempt - 1]);
      try {
        return await attemptLoad(attempt > 0);
      } catch (error) {
        lastError = error;
        moduleUrl ??= extractModuleUrl(error);
      }
    }
    markChunkLoadError(lastError);
    throw lastError;
  };

  const loadOnce = (): Promise<{ default: T }> => {
    pending ??= loadWithBackoff().catch((error) => {
      pending = null; // a later preload/mount/reset starts a fresh ladder
      failedResets.add(reset);
      throw error;
    });
    return pending;
  };

  let Inner: LazyExoticComponent<T> = lazy(loadOnce);
  const reset = (): void => {
    Inner = lazy(loadOnce);
  };

  // Stable outer identity; the swappable inner lazy carries the import state.
  const component = forwardRef<unknown, ComponentProps<T>>((props, ref) =>
    createElement(Inner, (ref == null ? props : { ...props, ref }) as ComponentProps<T>)
  ) as unknown as PreloadableLazyComponent<T>;
  component.preload = () =>
    loadOnce().then(
      () => undefined,
      () => undefined // warm-up only — the render path surfaces real failures
    );
  return component;
}
